// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { updateGamesCache } from '@/utils/game-info-api-cache';

import { RootState } from '..';
import { hasGameDataSources, sortGameDataSources } from './playerGamesOrder';

import { hardodedJackpotResp } from '@/_temp/hardcodedJackpotResp';
import urlonStringify from '@/utils/urlon';

const DEBUG = false;

type FetchConfigProps = {
  ids: string[];
};

type FetchConfigResult = {
  data: Map<string, any[]>;
  success: boolean;
};

type FetchConfigError = {
  rejectValue: {
    error: string;
  };
};

export function onlyKeys(obj: any, includeKeys: string[]) {
  const result: any = {};
  Object.keys(obj).forEach((key: any) => {
    if (includeKeys.indexOf(key) > -1) {
      result[key] = obj[key];
    }
  });
  return result;
}

const apiUrl = window.config.dataSourceApiUrl;

const IS_RECOMMENDED_GAMES_ENABLED = window?.config?.recommendedGamesEnabled === '1';
const IS_REELS_RECOMMENDED_GAMES_ENABLED = window?.config?.casinoExplorer?.isReelsRecommendedGamesEnabled === '1';

const requestingSources: Record<string, boolean> = {};
const reqCache: Record<string, boolean> = {};

export const checkIDSInCache = (ids: string[]) => {
  const notInCache: string[] = [];
  ids.forEach((id) => {
    if (!reqCache?.[id]) {
      notInCache.push(id);
      reqCache[id] = true;
    }
  });
  return notInCache;
};

export const clearCacheForIDs = (ids: string[]) => {
  ids.forEach((id) => {
    if (reqCache?.[id]) {
      delete reqCache[id];
    }
  });
};

export const clearRequestSourcesCache = () => {
  Object.keys(reqCache ?? {}).forEach((key) => {
    delete reqCache[key];
  });
};
export const getRequestSourcesCache = () => {
  return reqCache;
};

const artStyleAttributes: any = {};

const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);
const attributes = [
  'site_art_style_banner_set_id',
  'site_art_style_decorations_set_id',
  'site_art_style_iconography_set_id',
];
attributes.forEach((attr) => {
  if (urlParams.has(attr)) {
    artStyleAttributes[attr] = urlParams.get(attr);
  }
});

export const fetchSources = createAsyncThunk<FetchConfigResult, FetchConfigProps, FetchConfigError>(
  'dataSources/list',
  async ({ ids }, { dispatch, getState, rejectWithValue }) => {
    try {
      const state: RootState = getState() as RootState;

      const authToken = state.authentication.access_token
        ? state.authentication.access_token
        : window.config.guestToken
          ? window.config.guestToken
          : '';

      if (!authToken) {
        //console.error("Couldn't fetch sources - no auth token", ids);
        return rejectWithValue({
          error: "Couldn't fetch sources - no auth token",
        });
      }

      const clearedIds = checkIDSInCache(ids);
      if (!clearedIds.length) {
        return { data: [], success: true };
      }

      const data: any = {
        ids: clearedIds,
      };

      if (Object.keys(artStyleAttributes).length) {
        data['attributes'] = { ...artStyleAttributes };
      }

      const qParams = urlonStringify(data);

      if (requestingSources[qParams]) {
        return { data: [], success: true };
      }
      requestingSources[qParams] = true;

      const response = await axios.get(`${apiUrl}/resolve/sources`, {
        headers: {
          Authorization: 'Bearer ' + authToken,
        },
        params: {
          q: qParams,
          tid: window.config.clientId ?? 0,
          sid: window.config.siteId ?? 0,
        },
      });

      //clearCacheForIDs(clearedIds);

      // Sometimes the API replies with "no response set" string resulting in each string characted to be added to the object in state
      if (response.data && typeof response.data === 'object') {
        let data = { ...response.data };

        // for staging environment, we need to replace the jackpot data with hardcoded data since we don`t receive any provider data
        if (
          data[window.config.dataSourceAllJackpots] &&
          window.config.environment === 'staging' &&
          window.config.enableLocalJackpots === '1'
        ) {
          if (!data[window.config.dataSourceAllJackpots]?.data?.length) {
            data[window.config.dataSourceAllJackpots] = hardodedJackpotResp;
          } else {
            hardodedJackpotResp.data.forEach((group: any) => {
              const exists = data[window.config.dataSourceAllJackpots].data.find((g: any) => g.group === group.group);
              if (!exists) {
                data[window.config.dataSourceAllJackpots].data.push(group);
              }
            });
          }
        }

        if ((IS_RECOMMENDED_GAMES_ENABLED || IS_REELS_RECOMMENDED_GAMES_ENABLED) && hasGameDataSources(data)) {
          data = { ...data, ...sortGameDataSources(data, state.playerGamesOrder.items) };
        }

        delete requestingSources[qParams];

        delete data['__debug'];

        Object.keys(data ?? {}).forEach((key) => {
          if (data?.[key]?.element_type?.type_id === 'dynamic-page-element') {
            const noRefresh: any = [];
            const withRefresh: any = [];
            const playerBased: any = [];

            data?.[key]?.data.forEach((el: any) => {
              Object.keys(el ?? {}).forEach((k) => {
                if (el?.[k]?.dsId?.ref_id) {
                  const dsId = el?.[k]?.dsId?.ref_id;

                  if (!state.dataSources.items?.[dsId]) {
                    const target_player = el?.[k]?.dsId?.meta?.target === 'player';
                    const hasRefresh = el?.[k]?.dsId?.meta?.auto_refresh === true;

                    if (target_player) {
                      playerBased.push(dsId);
                    } else if (hasRefresh) {
                      withRefresh.push(dsId);
                    } else {
                      noRefresh.push(dsId);
                    }
                  }
                }
              });
            });

            if (playerBased?.length) dispatch(fetchSources({ ids: playerBased }));
            if (withRefresh?.length) dispatch(fetchSources({ ids: withRefresh }));
            if (noRefresh?.length) dispatch(fetchSources({ ids: noRefresh }));
          }
        });

        return { data: data, success: true };
      }

      return rejectWithValue({
        error: "Couldn't fetch sources",
      });
    } catch (err: any) {
      const errResp = { error: err.toString() };

      return rejectWithValue(errResp);
    }
  },
);

export interface ConfigReducer {
  items: any;
  loading: boolean;
  loaded: boolean;
  error: any;
}

export const dataSourcesSlice = createSlice({
  name: 'dataSources',
  initialState: <ConfigReducer>{
    items: {},
    loading: false,
    loaded: false,
    error: null,
  },
  reducers: {
    clearDataSources: (state) => {
      state.items = {};
      state.loading = false;
      state.loaded = false;
      state.error = null;
    },
    sortSlotGameDataSources: (state, action) => {
      if ((IS_RECOMMENDED_GAMES_ENABLED || IS_REELS_RECOMMENDED_GAMES_ENABLED) && hasGameDataSources(state.items)) {
        state.items = { ...state.items, ...sortGameDataSources(state.items, action.payload) };
      }
    },
    updateDataSourceJackpot: (state, action) => {
      Object.keys(state.items).forEach((key) => {
        if (
          key &&
          state.items[key] &&
          state.items[key].element_type &&
          state.items[key].element_type.type_id === 'module.jackpotV2Group'
        ) {
          const groupUpdate = action.payload.data;
          const data = [...JSON.parse(JSON.stringify(state.items[key].data))];

          groupUpdate.forEach((group: any) => {
            const groupIndex = data.findIndex((ob) => {
              if (ob.group === group.group) {
                return true;
              }
              return false;
            });

            if (groupIndex > -1) {
              group.jackpots.forEach((jackpot: any) => {
                const jackpotIndex = data[groupIndex].jackpots.data.findIndex((el: any) => {
                  return el.id === jackpot.id;
                });

                if (jackpotIndex > -1) {
                  const name = data[groupIndex].jackpots.data[jackpotIndex].accumulator_states.data.name;
                  let value = data[groupIndex].jackpots.data[jackpotIndex].accumulator_states.data.value;

                  let amount =
                    data[groupIndex].jackpots.data[jackpotIndex].accumulator_states.data.amounts?.[0]?.amount;

                  jackpot.accumulator_states.find((j: any) => {
                    if (j.name === name) {
                      value = j.value;
                      amount = j.amounts?.[0]?.amount;
                      return true;
                    }
                    return false;
                  });

                  data[groupIndex].jackpots.data[jackpotIndex].accumulator_states.data.value = value;

                  if (data[groupIndex].jackpots.data[jackpotIndex].accumulator_states.data.amounts?.length) {
                    data[groupIndex].jackpots.data[jackpotIndex].accumulator_states.data.amounts[0].amount = amount;
                  }

                  data[groupIndex].jackpots.data[jackpotIndex] = {
                    ...data[groupIndex].jackpots.data[jackpotIndex],
                    ...onlyKeys(data[groupIndex].jackpots.data[jackpotIndex], [
                      'state',
                      'state_time',
                      'start_date',
                      'win_trigger_type',
                      'guaranteed_win',
                      'win_date',
                      'win_value',
                      'largest_win_amount',
                      'largest_win_date',
                      'largest_win_player',
                      'last_win_amount',
                      'last_win_date',
                      'last_win_player',
                    ]),
                  };
                }
              });
            }
          });

          state.items[key].data = data;
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSources.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;

        if (action.payload.success) {
          state.items = { ...state.items, ...action.payload.data };
        }

        if (action.payload.data) {
          Object.keys(action.payload.data).forEach((sId: any) => {
            const ds = action.payload.data[sId];
            if (ds != null && ds.element_type?.type_id === 'slot_game') {
              updateGamesCache(ds.data, sId);
            }
          });
        }

        DEBUG && console.log('fetchSources.fulfilled', action.payload);
      })
      .addCase(fetchSources.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;

        DEBUG && console.log('fetchSources.rejected', action.payload);
      });
  },
});

export const { clearDataSources, sortSlotGameDataSources, updateDataSourceJackpot } = dataSourcesSlice.actions;

export default dataSourcesSlice.reducer;
